import { useTranslation } from "react-i18next";
import type { JSX } from "react";
import { memo, useCallback, useMemo } from "react";

import type { IPaginationProps } from "./types";

import { PaginationItem } from "@components/molecules/pagination/paginationItem";
import { PaginationLink } from "@components/molecules/pagination/paginationLink";
import { PaginationBase } from "@components/molecules/pagination/paginationBase";
import { PaginationContent } from "@components/molecules/pagination/paginationContent";
import { PaginationPrevious } from "@components/molecules/pagination/paginationPrevious";
import { PaginationNext } from "@components/molecules/pagination/paginationNext";

export const Pagination = memo(function Pagination({
  totalPages,
  currentPage,
  onPageChange,
  nextLabel = "nextPage",
  beforeLabel = "beforePage",
}: IPaginationProps) {
  const { t } = useTranslation("common");

  // const media = useMaxMedia(Breakpoints.XS)

  // const maxPageNumbers = 3

  const startPage = useMemo(() => Math.max(currentPage - 1, 1), [currentPage]);
  const endPage = useMemo(
    () => Math.min(currentPage + 1, totalPages),
    [currentPage, totalPages],
  );

  const renderPageNumbers = useCallback((): JSX.Element[] => {
    const pages: JSX.Element[] = [];

    if (startPage > 1) {
      pages.push(
        <PaginationItem key={"start"} onClick={() => onPageChange(1)}>
          <PaginationLink>{"1"}</PaginationLink>
        </PaginationItem>,
      );
      if (startPage > 2) {
        pages.push(<PaginationItem key={"ellipsis1"}>{"…"}</PaginationItem>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem
          key={i.toString()}
          isActive={currentPage === i}
          onClick={() => onPageChange(i)}
        >
          <PaginationLink isActive={currentPage === i}>{i}</PaginationLink>
        </PaginationItem>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<PaginationItem key={"ellipsis2"}>{"…"}</PaginationItem>);
      }
      pages.push(
        <PaginationItem key={"end"} onClick={() => onPageChange(totalPages)}>
          <PaginationLink>{totalPages}</PaginationLink>
        </PaginationItem>,
      );
    }

    return pages;
  }, [currentPage, endPage, onPageChange, startPage, totalPages]);

  const pageNumbersComponent = useMemo(
    () => renderPageNumbers(),
    [renderPageNumbers],
  );

  return (
    <PaginationBase aria-label={"Page navigation"}>
      <PaginationContent>
        <PaginationPrevious
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          {t(beforeLabel)}
        </PaginationPrevious>
        {pageNumbersComponent}
        <PaginationNext
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        >
          {t(nextLabel)}
        </PaginationNext>
      </PaginationContent>
    </PaginationBase>
  );
});
